import { assets } from '@cmg/common';
import { Divider, Grid, InfoOutlinedIcon, Typography } from '@cmg/design-system';
import { FormattedDate, FormattedNumber } from '@cmg/i18n';
import React, { useMemo } from 'react';

import { useMatchingCmgEntityKeys } from '../../../../../../common/hooks/useMatchingCmgEntityKeys';
import { getOfferingTypeLabel } from '../../../../../../common/util/offering/offeringTypeHelpers';
import { OfferingStatusChip } from '../../../../../../design-system/components/data-display/offering-status-chip/OfferingStatusChip';
import { FilingType, OfferingStatus } from '../../../../../../graphql';
import { sectorLabels } from '../../../../../constants';
import { SideBar_OfferingDetails_OfferingPartsFragment } from '../../graphql';
import { SidePanelNavBarSection } from '../design-system';
import { CountryCodeLayout, Flag, OnPlatformLogo } from './design-system';

export type Props = {
  readonly loading?: boolean;
  publishedOffering: SideBar_OfferingDetails_OfferingPartsFragment;
};

const ConvertibleOfferingStatusIndicator: React.FC<Props> = props => {
  const { publishedOffering, loading } = props;
  const matchingCmgEntityKeys = useMatchingCmgEntityKeys();
  const isCurrentUsersFirmUnderwriting = useMemo(
    () =>
      publishedOffering?.syndicate?.managers.some(manager =>
        matchingCmgEntityKeys.includes(manager.cmgEntityKey)
      ),
    [publishedOffering, matchingCmgEntityKeys]
  );
  const pricingInstrument = useMemo(
    () =>
      publishedOffering?.instruments.find(
        instrument => instrument.id === publishedOffering.pricingInstrumentId
      ),
    [publishedOffering]
  );
  const filteredFilings = useMemo(
    () => publishedOffering?.terms.filings.filter(filing => filing.filingType !== FilingType.Final),
    [publishedOffering]
  );
  const mostRecentFiling = useMemo(
    () => filteredFilings?.[filteredFilings.length - 1],
    [filteredFilings]
  );
  const finalFiling = useMemo(
    () => publishedOffering?.terms.filings.find(filing => filing.filingType === FilingType.Final),
    [publishedOffering]
  );

  return (
    <div aria-label="Convertible Offering Status Section">
      <SidePanelNavBarSection isBusy={loading}>
        <Grid container spacing={1}>
          <Grid item sm={12}>
            <Typography variant="h3">{publishedOffering?.issuer.name}</Typography>
          </Grid>
          <Grid item sm={12}>
            <Typography variant="body2" color="text.secondary">
              {publishedOffering?.pricingInstrumentStockSymbol}
            </Typography>
          </Grid>
        </Grid>
        <Grid container spacing={1.5}>
          {publishedOffering?.isExecutingOnPlatform && (
            <React.Fragment>
              <Grid item sm={2}>
                <OnPlatformLogo
                  src={assets.logoNoText}
                  alt="This offering is being managed on the CMG Platform."
                />
              </Grid>
              <Grid item sm={10}>
                <Typography variant="body2" color="text.secondary">
                  This offering is being managed on the CMG Platform.
                </Typography>
              </Grid>
            </React.Fragment>
          )}
          {isCurrentUsersFirmUnderwriting && (
            <React.Fragment>
              <Grid item sm={2}>
                <Typography variant="body2" color="info.main">
                  <InfoOutlinedIcon fontSize="small" />
                </Typography>
              </Grid>
              <Grid item sm={10}>
                <Typography variant="body2" color="text.secondary">
                  My firm is participating in this deal
                </Typography>
              </Grid>
            </React.Fragment>
          )}
        </Grid>
      </SidePanelNavBarSection>
      <Divider />
      <SidePanelNavBarSection isBusy={loading}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              Type
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="highlight2" color="text.primary">
              {getOfferingTypeLabel(publishedOffering!.type)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              Status
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <OfferingStatusChip status={publishedOffering!.status} />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              Sector
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="highlight2" color="text.primary">
              {publishedOffering?.issuer.sector
                ? sectorLabels[publishedOffering.issuer.sector]
                : null}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              Exch. Region
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="highlight2" color="text.primary">
              {pricingInstrument?.region ? pricingInstrument?.region : '-'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              Exch. Country
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <CountryCodeLayout>
              <Flag countryCode={pricingInstrument?.countryCode} />
              <Typography variant="highlight2" color="text.primary">
                {pricingInstrument?.countryCode}
              </Typography>
            </CountryCodeLayout>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              Currency
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="highlight2" color="text.primary">
              {pricingInstrument?.currencyCode}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              Principal Amnt
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="highlight2" color="text.primary">
              <FormattedNumber
                value={publishedOffering.sizeInCurrency}
                format="currencyCompact"
                currency={publishedOffering.pricingCurrencyCode}
              />
            </Typography>
          </Grid>
          {(publishedOffering.status === OfferingStatus.Live ||
            publishedOffering.status === OfferingStatus.Filed ||
            publishedOffering.status === OfferingStatus.Postponed) && (
            <React.Fragment>
              <Grid item xs={6}>
                <Typography variant="specialLabel" color="text.secondary">
                  Coupon Talk
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="highlight2" color="text.primary">
                  <FormattedNumber value={mostRecentFiling?.couponTalkLow} format="percent" />–
                  <FormattedNumber value={mostRecentFiling?.couponTalkHigh} format="percent" />
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="specialLabel" color="text.secondary">
                  Premium Talk
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="highlight2" color="text.primary">
                  <FormattedNumber value={mostRecentFiling?.premiumTalkLow} format="percent" />–
                  <FormattedNumber value={mostRecentFiling?.premiumTalkHigh} format="percent" />
                </Typography>
              </Grid>
            </React.Fragment>
          )}

          {publishedOffering.status === OfferingStatus.Priced && (
            <React.Fragment>
              <Grid item xs={6}>
                <Typography variant="specialLabel" color="text.secondary">
                  Coupon
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="highlight2" color="text.primary">
                  <FormattedNumber value={finalFiling?.coupon} format="percent" />
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="specialLabel" color="text.secondary">
                  Premium
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="highlight2" color="text.primary">
                  <FormattedNumber value={finalFiling?.premium} format="percent" />
                </Typography>
              </Grid>
            </React.Fragment>
          )}

          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              Maturity Date
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="highlight2" color="text.primary">
              <FormattedDate
                value={publishedOffering?.terms.maturityDate}
                format="literal"
                timeZone="UTC"
              />
            </Typography>
          </Grid>
        </Grid>
      </SidePanelNavBarSection>
    </div>
  );
};

export default ConvertibleOfferingStatusIndicator;
